"use client";

import { useBooking } from "@/contexts/BookingContext";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  User,
  Users,
  UserCheck,
  ChevronDown,
  Check,
  Search,
  ChevronRight,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { BookingStep, BookingService } from "@/types/booking";
import { formatDuration, formatPrice, toSentenceCase } from "@/lib/utils";
import { useState, useEffect } from "react";
import { useScrollToTop } from "@/hooks/useScrollToTop";
import CartSummary from "@/components/booking/CartSummary";
import { useGetServiceStaffs } from "@/lib/apollo/hooks/booking";

type StaffMode = "any" | "one_for_all" | "per_service";

export default function StaffSelection() {
  useScrollToTop();
  const {
    selectedSalon,
    selectedServices,
    selectedStaff,
    setSelectedStaff,
    setSelectedServices,
    staffOption,
    setStaffOption,
    handleNextStep,
    businessInfo,
  } = useBooking();

  const [staffMode, setStaffMode] = useState<StaffMode | null>(null);
  const [showStaffSelection, setShowStaffSelection] = useState(false);
  const [selectedStaffId, setSelectedStaffId] = useState<string | null>(null);
  const [serviceStaffMap, setServiceStaffMap] = useState<
    Record<string, string>
  >({});

  const [searchQuery, setSearchQuery] = useState("");
  const [serviceSearchQuery, setServiceSearchQuery] = useState("");
  const [selectedServiceForStaff, setSelectedServiceForStaff] = useState<
    string | null
  >(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Query for service-specific staff
  const { data: serviceStaffsData, loading: serviceStaffsLoading } =
    useGetServiceStaffs(
      selectedServiceForStaff ? [selectedServiceForStaff] : []
    );

  // Initialize component state - always start with the main selection grid
  useEffect(() => {
    // Sync local state with context if staff are already selected (for display only)
    if (selectedServices.length > 0) {
      // Check if all services have the same staff (one_for_all mode)
      const firstServiceStaff = selectedServices[0]?.staff;
      const allSameStaff = selectedServices.every(
        (service) => service.staff?.id === firstServiceStaff?.id
      );

      if (allSameStaff && firstServiceStaff) {
        setStaffMode("one_for_all");
        setSelectedStaffId(firstServiceStaff.id);
        setShowStaffSelection(false); // Stay on main grid for one_for_all
        // Update services with staff pricing if not already set
        const updatedServices = selectedServices.map((service) => {
          if (!service.staffPrice && service.staff) {
            const salonService = selectedSalon?.services?.find(
              (s) => s.id === service.id
            );
            const serviceStaff = salonService?.serviceStaffs?.find(
              (ss) => ss.salonStaff?.id === service.staff?.id
            );
            return {
              ...service,
              staffPrice: serviceStaff?.price || service.price,
            };
          }
          return service;
        });
        if (updatedServices.some((s, i) => s !== selectedServices[i])) {
          setSelectedServices(updatedServices);
        }
      } else if (selectedServices.some((service) => service.staff)) {
        // // Some services have staff, set per_service mode for display
        // setStaffMode("per_service");
        // setShowStaffSelection(true); // Show per-service view when services have different staff
        // const staffMap: Record<string, string> = {};
        // const updatedServices = selectedServices.map((service) => {
        //   if (service.staff) {
        //     staffMap[service.id] = service.staff.id;
        //     if (!service.staffPrice) {
        //       const salonService = selectedSalon?.services?.find(
        //         (s) => s.id === service.id
        //       );
        //       const serviceStaff = salonService?.serviceStaffs?.find(
        //         (ss) => ss.salonStaff?.id === service.staff?.id
        //       );
        //       return {
        //         ...service,
        //         staffPrice: serviceStaff?.price || service.price,
        //       };
        //     }
        //   }
        //   return service;
        // });
        // setServiceStaffMap(staffMap);
        // if (updatedServices.some((s, i) => s !== selectedServices[i])) {
        //   setSelectedServices(updatedServices);
        // }
      } else {
        // No staff assigned yet, start with main selection grid
        setShowStaffSelection(false);
      }
    }
  }, []); // Empty dependency array ensures this runs only once on mount

  // Get unique staff members who can perform the selected services
  const availableStaff =
    selectedSalon?.salonStaffs?.filter(
      (staff) => staff.active && staff.canServeCustomers
    ) || [];

  // Get staff that can perform specific services
  const getStaffForService = (serviceId: string) => {
    // Find the service from selectedServices which already has the serviceStaffs data
    const service = selectedServices.find((s) => s.id === serviceId);

    // Also get the full service data from the salon which includes serviceStaffs
    const salonService = selectedSalon?.services?.find(
      (s) => s.id === serviceId
    );

    // Use the serviceStaffs from salon service data if available
    const serviceStaffs =
      salonService?.serviceStaffs || service?.serviceStaffs || [];

    // If service has no serviceStaffs array or it's empty, all staff can perform it
    if (!serviceStaffs || serviceStaffs.length === 0) {
      return availableStaff;
    }

    // Filter to only staff explicitly assigned to this service
    const serviceStaffIds = serviceStaffs
      .map((ss) => ss.salonStaff?.id)
      .filter(Boolean);

    // If no valid staff IDs found, return all staff
    if (serviceStaffIds.length === 0) {
      return availableStaff;
    }

    // Get staff from availableStaff that can perform this service
    const staffFromSalon = availableStaff.filter((staff) =>
      serviceStaffIds.includes(staff.id)
    );

    // Also check if we have service-specific staff data from GraphQL query
    if (
      selectedServiceForStaff === serviceId &&
      serviceStaffsData?.serviceStaffs?.[0]?.salonStaffsForBooking
    ) {
      const graphqlStaff =
        serviceStaffsData.serviceStaffs[0].salonStaffsForBooking;

      // Merge both sources, prioritizing GraphQL data for consistency
      const allStaff = [...staffFromSalon];

      graphqlStaff.forEach((graphqlStaffMember: any) => {
        if (
          !allStaff.find((s) => s.id === graphqlStaffMember.id) &&
          graphqlStaffMember.active &&
          graphqlStaffMember.canServeCustomers
        ) {
          // Add GraphQL staff if not already in the list and can serve customers
          allStaff.push({
            id: graphqlStaffMember.id,
            user: graphqlStaffMember.user,
            specialty: graphqlStaffMember.specialty,
            active: graphqlStaffMember.active,
            canServeCustomers: graphqlStaffMember.canServeCustomers,
          });
        }
      });

      return allStaff;
    }

    return staffFromSalon;
  };

  // Get staff that can perform ALL selected services
  const getStaffForAllServices = () => {
    if (selectedServices.length === 0) return availableStaff;

    // Find staff that can do all services
    return availableStaff.filter((staff) => {
      return selectedServices.every((service) => {
        // Get the full service data from salon which includes serviceStaffs
        const salonService = selectedSalon?.services?.find(
          (s) => s.id === service.id
        );
        const serviceStaffs =
          salonService?.serviceStaffs || service.serviceStaffs || [];

        // If no service staff restrictions, any staff can perform it
        if (!serviceStaffs || serviceStaffs.length === 0) return true;

        // Check if this staff is in the serviceStaffs list
        return serviceStaffs.some((ss) => ss.salonStaff?.id === staff.id);
      });
    });
  };

  const handleModeSelection = (mode: StaffMode) => {
    setStaffMode(mode);
    setSelectedStaffId(null);
    setServiceStaffMap({});
    setSearchQuery(""); // Clear search when changing modes
    setServiceSearchQuery(""); // Clear service search when changing modes

    if (mode === "any") {
      // For "any available", clear staff and go directly to date/time
      setStaffOption("no_preference");
      setSelectedStaff(null);
      // Clear staff from all services
      const updatedServices = selectedServices.map(
        (service: BookingService) => ({
          ...service,
          staff: null,
          staffIds: [],
        })
      );
      setSelectedServices(updatedServices);
    } else if (mode === "per_service") {
      // For per-service, show the detailed service selection
      setStaffOption("specific");
      setShowStaffSelection(true);

      // Initialize serviceStaffMap with any existing staff assignments
      const existingStaffMap: Record<string, string> = {};
      selectedServices.forEach((service) => {
        if (service.staff) {
          existingStaffMap[service.id] = service.staff.id;
        }
      });

      if (Object.keys(existingStaffMap).length > 0) {
        setServiceStaffMap(existingStaffMap);
      }
    } else {
      // For one_for_all, stay on the main grid (don't change showStaffSelection)
      setStaffOption("specific");
    }
  };

  const handleStaffSelection = (staffId: string) => {
    // Set mode to one_for_all when selecting a specific staff
    setStaffMode("one_for_all");
    setSelectedStaffId(staffId);
    setStaffOption("specific");

    if (staffId === "any") {
      setSelectedStaff(null);
      // Update all services to have no specific staff (any available)
      const updatedServices = selectedServices.map(
        (service: BookingService) => ({
          ...service,
          staff: null,
          staffIds: [],
          staffPrice: service.price, // Reset to base price
        })
      );
      setSelectedServices(updatedServices);
    } else {
      const staff = availableStaff.find((s) => s.id === staffId);
      setSelectedStaff(staff || null);

      // Update all services to use this staff with their specific pricing
      const updatedServices = selectedServices.map(
        (service: BookingService) => {
          // Get the service from salon data to access serviceStaffs pricing
          const salonService = selectedSalon?.services?.find(
            (s) => s.id === service.id
          );
          const serviceStaff = salonService?.serviceStaffs?.find(
            (ss) => ss.salonStaff?.id === staffId
          );

          return {
            ...service,
            staff: staff || null,
            staffIds: staff ? [staff.id] : [],
            staffPrice: serviceStaff?.price || service.price, // Use staff-specific price if available
          };
        }
      );
      setSelectedServices(updatedServices);
    }
  };

  const handleServiceStaffSelection = (serviceId: string, staffId: string) => {
    setServiceStaffMap((prev) => {
      const newMap = { ...prev, [serviceId]: staffId };

      return newMap;
    });

    // Update the selectedServices in the booking context with staff pricing
    // First try to find staff in availableStaff, then fall back to serviceStaffsData
    let staff = availableStaff.find((s) => s.id === staffId);

    // If not found in availableStaff, try to get from serviceStaffsData
    if (
      !staff &&
      serviceStaffsData?.serviceStaffs?.[0]?.salonStaffsForBooking
    ) {
      const dialogStaff =
        serviceStaffsData.serviceStaffs[0].salonStaffsForBooking.find(
          (s: any) => s.id === staffId
        );
      if (dialogStaff) {
        // Create a staff object that matches the expected structure
        staff = {
          id: dialogStaff.id,
          user: dialogStaff.user,
          specialty: dialogStaff.specialty,
          active: dialogStaff.active,
          canServeCustomers: dialogStaff.canServeCustomers,
        };
      }
    }

    if (!staff) {
      return;
    }

    const updatedServices = selectedServices.map((service: BookingService) => {
      if (service.id === serviceId) {
        // Get the service from salon data to access serviceStaffs pricing
        const salonService = selectedSalon?.services?.find(
          (s) => s.id === serviceId
        );
        const serviceStaff = salonService?.serviceStaffs?.find(
          (ss) => ss.salonStaff?.id === staffId
        );

        const updatedService = {
          ...service,
          staff: staff || null,
          staffIds: staff ? [staff.id] : [],
          staffPrice: serviceStaff?.price || service.price, // Use staff-specific price if available
        };

        return updatedService;
      }
      return service;
    });

    setSelectedServices(updatedServices);
  };

  const openStaffDialog = (serviceId: string) => {
    setSelectedServiceForStaff(serviceId);
    setIsDialogOpen(true);
    setServiceSearchQuery(""); // Clear search when opening dialog
  };

  const closeStaffDialog = () => {
    setIsDialogOpen(false);
    setSelectedServiceForStaff(null);
    setServiceSearchQuery(""); // Clear search when closing dialog
  };

  const handleStaffSelectionInDialog = (staffId: string) => {
    if (selectedServiceForStaff) {
      // Ensure the staff is properly assigned to the service
      handleServiceStaffSelection(selectedServiceForStaff, staffId);

      // Also update the local serviceStaffMap to ensure UI consistency
      setServiceStaffMap((prev) => {
        const newMap = { ...prev, [selectedServiceForStaff]: staffId };

        return newMap;
      });

      closeStaffDialog();
    }
  };

  const handleContinue = () => {
    // Save per-service staff selections if needed
    handleNextStep(BookingStep.TIME_AND_DATE);
  };

  const handleBack = () => {
    if (showStaffSelection && staffMode === "per_service") {
      // If we're in the per-service detail view, go back to the main selection grid

      setShowStaffSelection(false);
      setStaffMode(null);
      setSelectedStaffId(null);
      setServiceStaffMap({});
      setSearchQuery("");
      setServiceSearchQuery("");
    } else {
      setStaffMode(null);
      setShowStaffSelection(false);
      setSelectedStaffId(null);
      setServiceStaffMap({});
      setSearchQuery("");
      setServiceSearchQuery("");
      setSelectedStaff(null);
      setStaffOption("no_preference");
      handleNextStep(BookingStep.INITIAL);
    }
  };

  const canContinue = () => {
    if (!staffMode) return false; // Must select a mode first
    if (staffMode === "any") return true;
    if (staffMode === "one_for_all") return selectedStaffId !== null;
    if (staffMode === "per_service") {
      // Check if all services have staff assigned
      const canContinueResult = selectedServices.every((service) => {
        const hasStaff = serviceStaffMap[service.id] || service.staff;

        return hasStaff;
      });

      return canContinueResult;
    }
    return false;
  };

  const staffForAllServices = getStaffForAllServices();

  // Filter staff based on search query
  const filteredStaffForAllServices = staffForAllServices.filter((staff) => {
    if (!searchQuery.trim()) return true;
    const fullName = staff.user?.fullName || staff.user?.firstName || "";
    const specialty = staff.specialty || "";
    const searchTerm = searchQuery.toLowerCase();
    return (
      fullName.toLowerCase().includes(searchTerm) ||
      specialty.toLowerCase().includes(searchTerm)
    );
  });

  return (
    <div className="min-h-screen bg-grey-5 pb-24 lg:pb-0">
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex gap-8">
          {/* Left Column - Main Content */}
          <div className="flex-1 max-w-4xl space-y-4">
            {!showStaffSelection && (
              <>
                <div className="relative mb-4">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-5 w-5 text-grey-400" />
                  </div>
                  <input
                    type="text"
                    placeholder="Search by name or specialty..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="block w-full pl-10 pr-3 py-3 border placeholder:text-sm border-grey-75 rounded-lg focus:ring-2 focus:ring-green-300 focus:border-green-300 outline-none transition-colors bg-white text-grey-900 placeholder-grey-500"
                  />
                </div>

                {/* Always show the basic options grid */}
                <div
                  className={`
                    grid gap-2
                    grid-cols-2
                    sm:grid-cols-3
                    lg:grid-cols-4
                  `}
                >
                  {businessInfo?.selectAnyStaffForOnlineBooking == true ? (
                    <StaffTile
                      title="Any Available"
                      subtitle="Best availability"
                      icon={
                        <div className="w-14 h-14 sm:w-12 sm:h-12 bg-grey-100 rounded-full flex items-center justify-center">
                          <Users className="h-5 w-5 text-grey-600" />
                        </div>
                      }
                      isSelected={staffMode === "any"}
                      onClick={() => handleModeSelection("any")}
                    />
                  ) : null}
                  {(selectedServices.length > 1 ||
                    staffForAllServices.length === 0) && (
                    <StaffTile
                      title="Per service"
                      subtitle="Select per service"
                      icon={
                        <div className="w-14 h-14 sm:w-12 sm:h-12 bg-grey-100 rounded-full flex items-center justify-center">
                          <User className="h-5 w-5 text-grey-600" />
                        </div>
                      }
                      isSelected={staffMode === "per_service"}
                      onClick={() => handleModeSelection("per_service")}
                    />
                  )}

                  {/* Always show individual staff who can perform all selected services */}
                  {filteredStaffForAllServices.map((staff) => (
                    <StaffTile
                      key={staff.id}
                      staff={staff}
                      isSelected={selectedStaffId === staff.id}
                      onClick={() => handleStaffSelection(staff.id)}
                    />
                  ))}
                </div>

                {/* Show search no results message */}
                {searchQuery &&
                  filteredStaffForAllServices.length === 0 &&
                  staffForAllServices.length > 0 && (
                    <div className="bg-white rounded-lg p-8 text-center">
                      <Search className="h-12 w-12 text-grey-300 mx-auto mb-3" />
                      <p className="text-grey-600">
                        No professionals found matching "{searchQuery}"
                      </p>
                      <p className="text-sm text-grey-500 mt-1">
                        Try a different search term
                      </p>
                    </div>
                  )}

                {/* Show no staff available message when no staff can perform all services */}
                {/* {staffForAllServices.length === 0 && !searchQuery && (
                  <div className="bg-white rounded-lg p-8 text-center mt-4">
                    <User className="h-12 w-12 text-grey-300 mx-auto mb-3" />
                    <p className="text-grey-600">
                      {selectedServices.length === 1
                        ? "No professionals available for this service"
                        : "No single professional can perform all selected services"}
                    </p>
                    <p className="text-sm text-grey-500 mt-1">
                      {selectedServices.length === 1
                        ? "You can select 'Any Available' or contact the salon"
                        : "You can select 'Any Available' or choose per-service professionals"}
                    </p>
                  </div>
                )} */}
              </>
            )}

            {/* Per Service - Show services list */}
            {showStaffSelection && staffMode === "per_service" && (
              <div className="space-y-4">
                <div className="text-sm text-grey-600 font-medium px-2 mb-4">
                  Select professionals for each service
                </div>

                {selectedServices.map((service) => {
                  const selectedStaffId = serviceStaffMap[service.id];
                  const selectedStaff = selectedStaffId
                    ? availableStaff.find((s) => s.id === selectedStaffId)
                    : null;

                  return (
                    <div
                      key={service.id}
                      className="bg-white rounded-lg border border-grey-100"
                    >
                      <div
                        onClick={() => openStaffDialog(service.id)}
                        className="p-4 cursor-pointer hover:bg-grey-50 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3">
                              <div className="w-12 h-12 bg-green-50 rounded-full flex items-center justify-center flex-shrink-0">
                                <User className="h-5 w-5 text-green-300" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <h3 className="text-base font-semibold text-grey-900 mb-1">
                                  {service.name}
                                </h3>
                                <div className="flex items-center gap-2 text-sm text-grey-600 mb-2">
                                  <span className="text-sm text-grey-300 font-medium">
                                    {formatDuration(service.duration)}
                                  </span>

                                  <div className="flex items-center gap-2">
                                    <div className="w-6 h-6 bg-green-50 rounded-full flex items-center justify-center">
                                      <span className="text-xs font-semibold text-green-600">
                                        {selectedStaff?.user?.firstName?.[0]?.toUpperCase() ||
                                          "S"}
                                      </span>
                                    </div>
                                    <span className="text-sm text-green-700 font-medium">
                                      {selectedStaff?.user?.fullName ||
                                        selectedStaff?.user?.firstName ||
                                        "Any professional"}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {selectedStaff && (
                              <div className="w-5 h-5 bg-green-300 rounded-full flex items-center justify-center">
                                <Check className="w-3 h-3 text-white" />
                              </div>
                            )}
                            <ChevronRight className="h-5 w-5 text-grey-400" />
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Right Column - Cart Summary (Desktop Only) */}
          <div className="hidden lg:block w-96">
            <div className="sticky top-6">
              <CartSummary
                customButton={{
                  text: "Continue to Date & Time",
                  onClick: handleContinue,
                  disabled: !canContinue(),
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Continue Button - Fixed at bottom for desktop */}
      {((staffMode === "one_for_all" && !showStaffSelection) ||
        staffMode === "per_service" ||
        staffMode === "any") && (
        <div className="fixed bottom-0 left-0 right-0 bg-white p-4">
          <div className="max-w-4xl mx-auto">
            <Button
              onClick={handleContinue}
              disabled={!canContinue()}
              className="w-full bg-green-300 hover:bg-green-400 text-white h-12 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Continue to Date & Time
            </Button>
          </div>
        </div>
      )}

      {((staffMode === "one_for_all" && !showStaffSelection) ||
        (showStaffSelection && staffMode === "per_service")) && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-grey-100 p-4 lg:hidden">
          <div className="flex gap-3">
            <Button
              onClick={handleBack}
              variant="outline"
              className="flex-1 border-grey-300"
            >
              Back
            </Button>
            <Button
              onClick={handleContinue}
              disabled={!canContinue()}
              className="flex-1 bg-green-300 hover:bg-green-400 text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Continue
            </Button>
          </div>
        </div>
      )}

      {/* Staff Selection Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-md bg-white">
          {selectedServiceForStaff &&
            (() => {
              const service = selectedServices.find(
                (s) => s.id === selectedServiceForStaff
              );

              // Use staff data from GraphQL query
              // The GraphQL response structure is: { serviceStaffs: [ { salonStaffsForBooking: [...] } ] }
              // So we need to extract the first element's salonStaffsForBooking array.
              const staffForService =
                serviceStaffsData?.serviceStaffs?.[0]?.salonStaffsForBooking ||
                [];

              // Filter staff based on search
              const filteredStaffForService = staffForService.filter(
                (staff: any) => {
                  if (!serviceSearchQuery.trim()) return true;
                  const fullName =
                    staff.user?.fullName || staff.user?.firstName || "";
                  const specialty = staff.specialty || "";
                  const searchTerm = serviceSearchQuery.toLowerCase();
                  return (
                    fullName.toLowerCase().includes(searchTerm) ||
                    specialty.toLowerCase().includes(searchTerm)
                  );
                }
              );

              return (
                <>
                  <DialogHeader>
                    <DialogTitle className="text-lg font-semibold">
                      Select Professional
                    </DialogTitle>
                    <DialogDescription className="text-sm text-grey-600">
                      Choose a professional for {service?.name}
                    </DialogDescription>
                  </DialogHeader>

                  <div className="space-y-4">
                    {/* Search Input */}
                    {staffForService.length > 3 && (
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Search className="h-4 w-4 text-grey-400" />
                        </div>
                        <input
                          type="text"
                          placeholder="Search professionals..."
                          value={serviceSearchQuery}
                          onChange={(e) =>
                            setServiceSearchQuery(e.target.value)
                          }
                          className="block w-full pl-9 pr-3 py-2 border border-grey-100 rounded-lg focus:ring-2 focus:ring-green-300 focus:border-green-300 outline-none transition-colors text-sm"
                        />
                      </div>
                    )}

                    {/* Staff List */}
                    <div className="max-h-60 overflow-y-auto space-y-2">
                      {serviceStaffsLoading ? (
                        <div className="text-center py-6">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-300 mx-auto mb-2"></div>
                          <p className="text-sm text-grey-600">
                            Loading professionals...
                          </p>
                        </div>
                      ) : filteredStaffForService.length > 0 ? (
                        filteredStaffForService.map((staff: any) => (
                          <div
                            key={staff.id}
                            onClick={() =>
                              handleStaffSelectionInDialog(staff.id)
                            }
                            className={`p-3 rounded-lg border  cursor-pointer transition-all hover:shadow-sm ${
                              serviceStaffMap[selectedServiceForStaff] ===
                              staff.id
                                ? "border-green-300 bg-green-50  border-2 ring-green-300"
                                : "border-grey-100 hover:border-grey-300"
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 bg-green-50 rounded-full flex items-center justify-center">
                                  <span className="text-sm font-semibold text-green-600">
                                    {staff.user?.firstName?.[0]?.toUpperCase() ||
                                      "S"}
                                    {staff.user?.lastName?.[0]?.toUpperCase() ||
                                      ""}
                                  </span>
                                </div>
                                <div>
                                  <h4 className="text-sm font-medium text-grey-900">
                                    {staff.user?.fullName ||
                                      staff.user?.firstName ||
                                      "Staff Member"}
                                  </h4>
                                  {staff.specialty && (
                                    <p className="text-xs text-grey-600">
                                      {toSentenceCase(staff.specialty)}
                                    </p>
                                  )}
                                </div>
                              </div>
                              <div
                                className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                                  serviceStaffMap[selectedServiceForStaff] ===
                                  staff.id
                                    ? "bg-green-300 border-green-300"
                                    : "border-grey-300"
                                }`}
                              >
                                {serviceStaffMap[selectedServiceForStaff] ===
                                  staff.id && (
                                  <div className="w-2 h-2 bg-white rounded-full"></div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-6">
                          {serviceSearchQuery ? (
                            <>
                              <Search className="h-8 w-8 text-grey-300 mx-auto mb-2" />
                              <p className="text-sm text-grey-600">
                                No professionals found matching "
                                {serviceSearchQuery}"
                              </p>
                            </>
                          ) : (
                            <>
                              <User className="h-8 w-8 text-grey-300 mx-auto mb-2" />
                              <p className="text-sm text-grey-600">
                                No professionals available for this service
                              </p>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </>
              );
            })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Staff Tile Component
interface StaffTileProps {
  staff?: any;
  isSelected: boolean;
  onClick: () => void;
  title?: string;
  subtitle?: string;
  icon?: React.ReactNode;
}

function StaffTile({
  staff,
  isSelected,
  onClick,
  title,
  subtitle,
  icon,
}: StaffTileProps) {
  // Use custom props if provided, otherwise use staff data
  const displayTitle =
    title ||
    (staff?.user?.fullName
      ? `${staff.user.firstName || ""} ${staff.user.lastName?.[0] || ""}${
          staff.user.lastName ? "." : ""
        }`
      : staff?.user?.firstName || "Staff Member");

  const displaySubtitle =
    subtitle ||
    (staff?.specialty ? toSentenceCase(staff.specialty) : undefined);

  const displayIcon = icon || (
    <div className="w-14 h-14 sm:w-12 sm:h-12 bg-green-50 rounded-full flex items-center justify-center">
      <span className="text-sm sm:text-lg font-semibold text-green-600">
        {staff?.user?.firstName?.[0]?.toUpperCase() || "S"}
        {staff?.user?.lastName?.[0]?.toUpperCase() || ""}
      </span>
    </div>
  );

  return (
    <div
      onClick={onClick}
      className={`bg-white border-1 border-grey-75 rounded-lg py-6 px-1 sm:p-4 cursor-pointer transition-all ${
        isSelected ? "ring-2 ring-green-300 shadow-sm" : "hover:shadow-sm"
      }`}
    >
      <div className="flex flex-col items-center gap-3 sm:gap-6 text-center">
        {displayIcon}
        <div>
          <h3 className="text-sm sm:text-base font-semibold text-grey-900 leading-tight">
            {displayTitle}
          </h3>
          {displaySubtitle && (
            <p className="text-xs sm:text-sm text-grey-600">
              {displaySubtitle}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
